import { useEffect } from "react";

const SITE = "https://blezzings.co";
const DEFAULT_ROBOTS = "index, follow, max-image-preview:large, max-snippet:-1";
const abs = (u) => (u && u.startsWith("/") ? `${SITE}${u}` : u);

/**
 * Set per-route document metadata for SEO and social previews.
 * Sets title, description, canonical, robots, Open Graph / Twitter tags.
 * Injects JSON-LD for BreadcrumbList, Article, Service, FAQPage,
 * ProfessionalService (homepage) and Person (leadership) when provided.
 * Pass canonical: false to remove the canonical link (e.g. the 404 page).
 */
export function usePageMeta({
  title,
  description,
  canonical,
  robots,
  image,
  imageAlt,
  breadcrumbs,
  faqs,
  service,
  article,
  professionalService,
  person,
  webapp,
}) {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (attr, name, content) => {
      if (!content) return;
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("name", "description", description);
    setMeta("name", "robots", robots || DEFAULT_ROBOTS);
    const ogImage = abs(image);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", article?.headline ? "article" : "website");
    setMeta("property", "og:url", canonical || undefined);
    setMeta("property", "og:image", ogImage);
    setMeta("property", "og:image:secure_url", ogImage);
    setMeta("property", "og:image:alt", imageAlt);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", ogImage);
    if (article?.headline) {
      setMeta("property", "article:published_time", article.datePublished);
      setMeta("property", "article:modified_time", article.dateModified || article.datePublished);
    }

    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
      setMeta("property", "og:url", canonical);
    } else if (canonical === false) {
      document.head.querySelector('link[rel="canonical"]')?.remove();
    }

    const inject = (id, obj) => {
      document.getElementById(id)?.remove();
      if (!obj) return;
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = id;
      script.text = JSON.stringify(obj);
      document.head.appendChild(script);
    };

    // Breadcrumb JSON-LD
    inject(
      "ld-breadcrumb",
      breadcrumbs?.length
        ? {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: breadcrumbs.map((b, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: b.name,
              item: b.url,
            })),
          }
        : null
    );

    // Article JSON-LD
    inject(
      "ld-article",
      article?.headline
        ? {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.headline,
            description: article.description,
            image: article.image ? [article.image] : undefined,
            datePublished: article.datePublished,
            dateModified: article.dateModified || article.datePublished,
            mainEntityOfPage: article.url,
            author: {
              "@type": "Person",
              "@id": `${SITE}/#founder-khalid-kamal-rumi`,
              name: "Khalid Kamal Rumi",
              url: `${SITE}/leadership`,
            },
            publisher: {
              "@type": "Organization",
              name: "BLEZZINGS Growth Partners",
              url: SITE,
              logo: {
                "@type": "ImageObject",
                url: `${SITE}/favicon.png`,
              },
            },
          }
        : null
    );

    // Service JSON-LD
    inject(
      "ld-service",
      service?.name
        ? {
            "@context": "https://schema.org",
            "@type": "Service",
            name: service.name,
            description: service.description,
            url: service.url,
            serviceType: service.name,
            provider: {
              "@type": "Organization",
              name: "BLEZZINGS Growth Partners",
              url: SITE,
            },
            areaServed: "India",
          }
        : null
    );

    // FAQ JSON-LD — questions and answers must match visible text exactly.
    inject(
      "ld-faq",
      faqs?.length
        ? {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }
        : null
    );

    // ProfessionalService JSON-LD (homepage)
    inject(
      "ld-professional-service",
      professionalService
        ? {
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            name: "BLEZZINGS Growth Partners",
            alternateName: "BLEZZINGS",
            url: `${SITE}/`,
            logo: `${SITE}/apple-touch-icon.png`,
            telephone: "+91-80179-99746",
            email: "khalid@blezzings.co",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Whitefield, Bengaluru",
              addressRegion: "Karnataka",
              addressCountry: "IN",
            },
            areaServed: "India",
            sameAs: ["https://www.linkedin.com/company/blezzings"],
            founder: { "@id": `${SITE}/#founder-khalid-kamal-rumi` },
          }
        : null
    );

    // Person JSON-LD (leadership)
    inject(
      "ld-person",
      person?.name
        ? {
            "@context": "https://schema.org",
            "@type": "Person",
            "@id": `${SITE}/#founder-khalid-kamal-rumi`,
            name: person.name,
            jobTitle: person.jobTitle,
            worksFor: {
              "@type": "Organization",
              name: "BLEZZINGS Growth Partners",
              url: SITE,
            },
            alumniOf: person.alumniOf
              ? { "@type": "Organization", name: person.alumniOf }
              : undefined,
            sameAs: person.sameAs ? [person.sameAs] : undefined,
            award: person.awards?.length ? person.awards : undefined,
          }
        : null
    );

    // WebApplication JSON-LD (tools only)
    inject(
      "ld-webapp",
      webapp?.name
        ? {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: webapp.name,
            description: webapp.description,
            url: webapp.url,
            applicationCategory: "BusinessApplication",
            operatingSystem: "Any",
            offers: { "@type": "Offer", price: 0, priceCurrency: "INR" },
            provider: { "@type": "Organization", name: "BLEZZINGS Growth Partners", url: SITE },
          }
        : null
    );

    return () => {
      document.getElementById("ld-breadcrumb")?.remove();
      document.getElementById("ld-faq")?.remove();
      document.getElementById("ld-service")?.remove();
      document.getElementById("ld-article")?.remove();
      document.getElementById("ld-professional-service")?.remove();
      document.getElementById("ld-person")?.remove();
      document.getElementById("ld-webapp")?.remove();
      // Restore the static default robots directive.
      const r = document.head.querySelector('meta[name="robots"]');
      if (r) r.setAttribute("content", DEFAULT_ROBOTS);
    };
  }, [title, description, canonical, robots, image, imageAlt, breadcrumbs, faqs, service, article, professionalService, person, webapp]);
}
